import { useCallback, useEffect, useRef, useState } from 'react';
import {
  progressSample,
  watchProgressMark,
  PROGRESS_FLUSH_DELTA_SEC,
  PROGRESS_WRITE_INTERVAL_SEC,
  type WatchProgressEntry,
  type WatchProgressMark,
} from '@visious/core';
import type { ProgressLookup } from '../listing/model';
import { useWatchedHistory } from './useWatchedHistory';
import { systemClock } from '../platform/clock';
import { useSettings } from '../platform/settings';
import {
  putWatchProgress,
  readWatchProgress,
  readWatchProgressEntry,
  useWatchProgressStore,
} from '../platform/watchProgressStore';
import { hasHandedOver } from '../tabs/store';

/**
 * 4b's two React bindings over `playback/progress.ts`: **look up** a position to draw, and **record**
 * the one the player is at.
 *
 * They are a pair rather than one hook because they are used from opposite ends of the app and must not
 * be coupled: every listing screen reads, exactly one screen writes, and the writer deliberately does
 * *not* subscribe to the store — see {@link useWatchProgressRecording}.
 *
 * `useWatchedRecording` (3d) is the model for the writer's shape: numbers in, no player, no DOM, so the
 * decision stays core's and this file holds only what is about a component's lifetime.
 */

/**
 * The lookup every listing screen threads into its model, beside `nowEpochSec` — both sources of
 * "have I watched this" in one call, so no screen can thread one and forget the other.
 *
 * Subscribed to the store, so a video watched in another tab — or in this one, and then Back — shows
 * its bar without a reload. **`mark` is `null` for everything while the setting is off**, in one place
 * rather than per screen: that is what makes "off means no bar anywhere" true by construction instead
 * of by remembering it six times. The account's history is *not* gated on that setting — it stores no
 * position and says nothing about one, so switching resume positions off must not also switch off
 * knowing what has been watched.
 *
 * The clock is sampled once per mount rather than read per lookup. Reading it during render is impure —
 * the same render would produce different answers on a re-paint — and the only thing it decides is
 * whether an entry is past its one-year expiry, which is not a boundary a page open long enough to
 * matter was ever going to cross while somebody watched.
 *
 * The identity changes on every write, so a screen holding this in a `useMemo` recomputes its rows twelve
 * times a minute while a video plays. That is fine and worth stating rather than optimising: the only such
 * screen is Watch, whose rail cannot contain the video being watched, and which already re-renders four
 * times a second from the transport poll.
 */
export function useWatchProgressLookup(): ProgressLookup {
  const store = useWatchProgressStore();
  const enabled = useSettings().resumePlayback;
  const history = useWatchedHistory();
  const [nowEpochSec] = useState(() => Math.floor(systemClock.nowEpochMs() / 1000));

  return useCallback(
    (videoId: string) => ({
      mark: enabled ? watchProgressMark(store, videoId, nowEpochSec) : null,
      inHistory: history.has(videoId),
    }),
    [enabled, history, nowEpochSec, store],
  );
}

/**
 * The mark for one video, read **once**, for the Watch page's resume decision.
 *
 * Deliberately not `useWatchProgressLookup`, and this is the important half of the file. The resume
 * position is fed to the player as its start position, so it is part of the load: a value that changed
 * would re-mount the player and restart the video. Subscribing here would do exactly that — the writer
 * below updates the store every five seconds *of this video*, so the picture would jump back to where
 * it started roughly twelve times a minute.
 *
 * Read at first render rather than in an effect, because `localStorage` is synchronous and there is
 * nothing to wait for: an effect would let the player load at zero and then reload it at the stored
 * position, which is a visible restart and two `load()` calls.
 */
export function useStoredProgress(videoId: string): WatchProgressMark | null {
  const [mark] = useState<WatchProgressMark | null>(() =>
    watchProgressMark(readWatchProgress(), videoId, Math.floor(systemClock.nowEpochMs() / 1000)),
  );
  return mark;
}

export interface WatchProgressRecordingInput {
  readonly videoId: string;
  readonly positionSec: number;
  readonly durationSec: number | null;
  /** False while the plan is not playable — an upcoming premiere has no position to remember. */
  readonly playable: boolean;
  /** Paused is when a flush is worth it: the reader has stopped, at a position they chose. */
  readonly playing: boolean;
  /** §8's `save_player_pos`. `false` means nothing is written at all. */
  readonly enabled: boolean;
}

/**
 * Record the playhead as it moves — throttled by core, flushed on pause and on the way out.
 *
 * Three lifetime concerns, none of which is a decision about a *position*:
 *
 * - **Throttling.** The caller polls `status()` four times a second for the transport bar, and this
 *   reads the same two numbers off it rather than sampling again. Core decides whether a sample is
 *   worth a write (`PROGRESS_WRITE_INTERVAL_SEC` while playing); this only says which threshold applies.
 * - **A pause is a flush.** Stopping is the position a reader means, and it can be several seconds off
 *   the last throttled write. `PROGRESS_FLUSH_DELTA_SEC` rather than zero, so a paused video re-rendering
 *   does not write the same number repeatedly.
 * - **Leaving is a flush too**, which is what the cleanup with no dependencies is for: navigating to
 *   another video (React unmounts the keyed Watch screen) would otherwise lose up to five seconds. The
 *   latest sample is kept in a ref precisely so that cleanup can see it — a cleanup closed over the
 *   position would run on every sample instead of once.
 *
 * What is *not* covered: closing the tab outright. The 5-second write interval bounds the loss, and a
 * `pagehide` handler was left out on purpose — bfcache makes it fire on ordinary navigations too
 * (docs/specs/web-ui.md's note about state coming back), so it would be a second writer with a different
 * lifetime for five seconds of precision.
 *
 * And one unmount is deliberately **not** a flush: a tab that has handed its videos to another one
 * (`tabs/`). The reader is about to watch those videos in the tab that took them, so the position
 * this one had reached is not where they left off — writing it would put a resume marker in front of
 * them for a video they are still watching.
 */
export function useWatchProgressRecording(input: WatchProgressRecordingInput): void {
  const { videoId, positionSec, durationSec, playable, playing, enabled } = input;
  /**
   * What the store holds for this video, then what this mount last wrote. Seeded from storage rather
   * than from `null` because "is there already an entry" is what decides whether a position below the
   * watch threshold may be written at all — a reader who restarts a half-watched video must be able to
   * write the small number back (`progressSample`).
   */
  const [storedEntry] = useState(() => readWatchProgressEntry(videoId));
  const writtenRef = useRef<WatchProgressEntry | null>(storedEntry);
  /** The latest committed sample, so the unmount flush can see it without re-running per sample. */
  const latestRef = useRef(input);
  useEffect(() => {
    latestRef.current = input;
  });

  useEffect(() => {
    if (!enabled || !playable) return;
    const entry = progressSample({
      videoId,
      previous: writtenRef.current,
      positionSec,
      durationSec,
      nowEpochSec: Math.floor(systemClock.nowEpochMs() / 1000),
      minDeltaSec: playing ? PROGRESS_WRITE_INTERVAL_SEC : PROGRESS_FLUSH_DELTA_SEC,
    });
    if (entry === null) return;
    writtenRef.current = entry;
    putWatchProgress(entry);
  }, [durationSec, enabled, playable, playing, positionSec, videoId]);

  useEffect(
    () => () => {
      const last = latestRef.current;
      if (!last.enabled || !last.playable) return;
      // The handover's unmount is not a departure from the video — see above.
      if (hasHandedOver()) return;
      const entry = progressSample({
        videoId: last.videoId,
        previous: writtenRef.current,
        positionSec: last.positionSec,
        durationSec: last.durationSec,
        nowEpochSec: Math.floor(systemClock.nowEpochMs() / 1000),
        minDeltaSec: PROGRESS_FLUSH_DELTA_SEC,
      });
      if (entry !== null) putWatchProgress(entry);
    },
    [],
  );
}
